import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { IconButton } from '@material-ui/core'

import EmptyVoucherOption from './VoucherEmptyOption'
import useVoucherDataDetails from './useVoucherDataDetails'

import CloseIcon from '@material-ui/icons/Close'
import { makeStyles } from '@material-ui/core/styles'
import * as R from 'ramda'
import clsx from 'clsx'
import { htmlToPlainText } from 'libs/text'
import CustomStatelessSelector from 'app/components/selector/CustomStatelessSelector'
import { DATE_FORMAT_VOUCHER } from 'libs/dateUtils'
import moment from 'moment'
import useVoucherAttachment from './useVoucherAttachment'
import CreateVoucherDialog from './CreateVoucherDialog'

const VOUCHER_STATUS_ACTIVE = 1
const VOUCHER_STATUS_FULLY_REDEEMED = 2
const VOUCHER_STATUS_CANCEL = 3
const VOUCHER_STATUS_EXPIRED = 4

const getVoucherText = (formatMessage) => (voucherBody, status) => {
  let message = null

  if (status === VOUCHER_STATUS_ACTIVE) {
    message = { id: 'dealsPromotions.status.active', color: 'text-green' }
  }

  if (status === VOUCHER_STATUS_FULLY_REDEEMED) {
    message = { id: 'dealsPromotions.status.fullyRedeemed', color: 'text-red' }
  }

  if (status === VOUCHER_STATUS_CANCEL) {
    message = { id: 'dealsPromotions.status.canceled', color: 'text-red' }
  }

  if (status === VOUCHER_STATUS_EXPIRED) {
    message = { id: 'dealsPromotions.status.expired', color: 'text-red' }
  }

  if (R.isNil(message)) {
    return null
  }

  return (
    <div style={{ display: 'flex', overflow: 'hidden', maxWidth: '80%' }}>
      <p>{voucherBody}</p>
      <label className={clsx(message.color, 'px-2')}>
        {formatMessage({ id: message.id })}
      </label>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  linkCreateVoucher: {
    fontSize: 14,
    color: '#0073b1 !important',
    textDecoration: 'none !important',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  clearBtn: {
    right: '5%',
    top: 4,
    position: 'absolute',
    color: '#666',
    '@media (max-width:757px)': {
      right: '8%',
    },
    '@media (max-width:469px)': {
      right: '10%',
    },
  },
}))

function VoucherAttachSelector({
  // vouchers,
  label,
  selectedVoucher,
  voucherName,
  value,
  onClickRemove,
  disabled,
  isEditMode,
  isDraft,
  ...rest
}) {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [voucher, setVoucher] = useState({})
  const [voucherCreated, setVoucherCreated] = useState(false)
  const [voucherId, setVoucherId] = useState(undefined)

  const [
    onCreateSuccess,
    isOpen,
    initialModel,
    onOpenVoucherFormDialog,
    onCloseVoucherFormDialog,
  ] = useVoucherDataDetails()

  const getVoucherHtmlFromStatus = getVoucherText(formatMessage)

  const formatDateVoucher = (title, startDate, endDate) => {
    return `${htmlToPlainText(title)} (${formatMessage({
      id: 'createNotification.dialog.expiredDate',
    })}  ${moment(startDate).format(DATE_FORMAT_VOUCHER)} - ${moment(
      endDate,
    ).format(DATE_FORMAT_VOUCHER)})`
  }

  const { vouchers } = useVoucherAttachment({ isOpen })

  const succes = (model) => {
    setVoucherCreated(true)
    onCreateSuccess()
    setVoucherId(model.id)
  }

  useEffect(() => {
    if (isOpen) {
      setVoucherCreated(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (vouchers.length && voucherId) {
      setVoucher(R.find(R.propEq('id', voucherId))(vouchers))
    }
  }, [vouchers, voucherId])

  return (
    <div className="flex flex-row flex-1 relative">
      <CustomStatelessSelector
        {...rest}
        value={selectedVoucher}
        label={formatMessage({ id: 'dealsPromoation.attach.select.label' })}
        items={vouchers}
        name="voucher"
        voucher={voucher}
        voucherCreated={voucherCreated}
        isEditMode={isEditMode || isDraft}
        fullWidth
        renderValue={(selected) => {
          let titleVoucher =
            isEditMode || isDraft
              ? R.compose(
                  R.prop('title'),
                  R.find(R.propEq('id', selected.id)),
                )(vouchers)
              : selected.title

          if (
            R.includes(selected.status, [
              VOUCHER_STATUS_CANCEL,
              VOUCHER_STATUS_FULLY_REDEEMED,
              VOUCHER_STATUS_EXPIRED,
            ])
          ) {
            titleVoucher = R.compose(
              htmlToPlainText,
              R.prop('body'),
              R.head,
              R.prop('translates'),
            )(selected)
          }

          const voucherMessage = formatDateVoucher(
            titleVoucher,
            selected.startDate,
            selected.endDate,
          )

          return getVoucherHtmlFromStatus(voucherMessage, selected.status)
        }}
        validationErrors={{
          validateEmpty: formatMessage({
            id: 'error.form.required',
          }),
        }}
        firstMenu={
          <div>
            <EmptyVoucherOption
              onOpenVoucherFormDialog={onOpenVoucherFormDialog}
              vouchers={vouchers}
              styleClass={classes}
              formatMessage={formatMessage}
            />
          </div>
        }
        ValidComp={(voucher) => {
          return formatDateVoucher(
            voucher.title,
            voucher.startDate,
            voucher.endDate,
          )
        }}
      />
      <IconButton
        aria-label="delete"
        color="secondary"
        disabled={disabled}
        className={clsx(classes.button, classes.clearBtn)}
        onClick={onClickRemove}
      >
        <CloseIcon />
      </IconButton>
      <CreateVoucherDialog
        open={isOpen}
        onCreateSuccess={succes}
        initialModel={initialModel}
        onClose={onCloseVoucherFormDialog}
      />
    </div>
  )
}

export default React.memo(VoucherAttachSelector)
